import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { ActionTitleActions } from "@/components/actionTitle/ActionTitleActions";
import { sortCalendarItems } from "@/features/calendar/utils/sortItems";
import { useModals } from "@/features/modals/hooks";
import { UpcomingTaskItem } from "@/features/tasks/UpcomingTaskItem";
import { getCoworkerSettingsValue } from "@/features/workspace/customize/settings/CoworkerSettingsControl";
import { ONE_MONTH_END, ONE_MONTH_START } from "@/utils/dateUtils";
import {
  type Task,
  type WidgetSetting,
  type WidgetSize,
  useTasks,
} from "@repo/api";
import { ListView, Text, plusIcon } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { localizeDateRange } from "@repo/ui";
import { isBefore, startOfDay } from "date-fns";

import { ScrollContainer, WidgetContainer } from "./Components";
import { CARD_HEIGHT_NORMAL, CARD_HEIGHT_TALL } from "./constants";

const groupTasks = (tasks: Task[], toDate: Date) => {
  const today = startOfDay(new Date());

  return tasks.sort(sortCalendarItems).reduce(
    (memo, task) => {
      const taskDate = task.taskDate
        ? new Date(task.taskDate)
        : task.taskStartDateTime
          ? new Date(task.taskStartDateTime)
          : undefined;
      if (taskDate && isBefore(taskDate, today)) {
        // Don't show done tasks that are overdue
        if (!task.isDone) {
          memo.overdue.push(task);
        }
      } else if (taskDate && isBefore(taskDate, toDate)) {
        if (!task.isDone) {
          // Keep unfinished tasks
          memo.week.push(task);
        } else {
          if (
            task.completed?.taskCompletedDateTime &&
            !isBefore(new Date(task.completed.taskCompletedDateTime), today)
          ) {
            // Keep finished tasks that are completed today
            memo.week.push(task);
          }
        }
      } else {
        // Ignore tasks in the future?
      }

      return memo;
    },
    {
      overdue: [] as Task[],
      week: [] as Task[],
      unread: [] as Task[],
    }
  );
};

const Tasks = ({
  groupedTasks,
}: {
  groupedTasks: ReturnType<typeof groupTasks>;
}) => {
  const { pushModal } = useModals();

  if (groupedTasks.week.length === 0 && groupedTasks.overdue.length === 0) {
    return <Text>{t("prompt.no_tasks")}</Text>;
  }

  return (
    <ListView id="upcoming-task-list">
      {groupedTasks.overdue.map((task) => (
        <UpcomingTaskItem
          key={task.id}
          inset={false}
          editable={true}
          task={task}
          showProjectInfo
          onClick={() => {
            pushModal({
              type: "TASK",
              task,
              context: "WORKSPACE",
            });
          }}
        />
      ))}
      {groupedTasks.week.map((task) => (
        <UpcomingTaskItem
          key={task.id}
          inset={false}
          editable={true}
          task={task}
          showProjectInfo
          onClick={() => {
            pushModal({
              type: "TASK",
              task,
              context: "WORKSPACE",
            });
          }}
        />
      ))}
    </ListView>
  );
};

const getSettings = (settings?: WidgetSetting) => {
  const taskType = settings?.["TASK_TYPE"]?.toString().split("|");
  const dateRange = settings?.["DATE_RANGE"] as string[];
  const { email, teamId, assignedToTeam } = getCoworkerSettingsValue(
    settings?.COWORKER?.toString() ?? ""
  );

  return {
    taskType,
    email,
    teamId,
    assignedToTeam,
    fromDate: dateRange?.[0] ? new Date(dateRange?.[0]) : ONE_MONTH_START,
    toDate: dateRange?.[1] ? new Date(dateRange?.[1]) : ONE_MONTH_END,
    unitId: typeof settings?.STORE === "string" ? settings.STORE : undefined,
  };
};

interface Props {
  title: string;
  size: WidgetSize;
  settings?: WidgetSetting;
}
export const TaskWidget = ({ title, size, settings }: Props) => {
  const { pushModal } = useModals();
  const { dateFnsLocale } = useTranslation();

  const { taskType, email, teamId, assignedToTeam, fromDate, toDate, unitId } =
    getSettings(settings);

  const query = {
    fromDate,
    toDate,
    parentTaskType: taskType?.[0],
    subTaskType: taskType?.[1],
    emails: email,
    teamId: teamId ? teamId : undefined,
    assignedToTeam,
    unitId,
  };

  const { data: tasks = [] } = useTasks(query);
  const filteredTaks = tasks.filter(
    (task) => task.parentTaskType !== "role" && task.parentTaskType !== "away"
  );
  const groupedTasks = groupTasks(filteredTaks, toDate);

  return (
    <WidgetContainer
      size={size === "M" ? CARD_HEIGHT_TALL : CARD_HEIGHT_NORMAL}
    >
      <ActionTitle
        title={`${title} (${
          groupedTasks.week.length + groupedTasks.overdue.length
        })`}
        action={
          <ActionTitleActions
            actions={[
              {
                id: "create-task-action",
                text: t("action.create_task"),
                title: t("action.create_task"),
                icon: plusIcon,
                iconOnly: true,
                onClick: () => {
                  pushModal({
                    type: "TASK",
                    context: "WORKSPACE",
                  });
                },
              },
            ]}
          />
        }
      />

      <Text>{localizeDateRange(fromDate, toDate, dateFnsLocale)}</Text>

      <ScrollContainer overflowY="auto">
        <Tasks groupedTasks={groupedTasks} />
      </ScrollContainer>
    </WidgetContainer>
  );
};
